import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import {
  OFFER_STATUSES,
  OPEN,
  OPPORTUNITY_STATUSES,
  READY_TO_SEND,
  ORDERED_AS_MATERIAL,
  isConvertedStatus,
  isOrderedStatus,
} from 'services/enums/offers/Statuses';
import {
  MATERIAL_OFFER,
  OFFER,
  OPPORTUNITY,
  PRELIMINARY_OFFER,
} from 'services/enums/offers/offerTypes';
import { getStatusTranslationPath } from 'services/offers/offerStatusLabel';
import { fetchStatusTypes } from 'store/offers/actions';

// The choice in the status list that makes a material offer; never saved as a status
const TO_MATERIAL_OFFER = 'to-material-offer';

const Status = ({
  disabled,
  formState,
  handleFormState,
  initialStatus,
  offerId,
  offerType,
  sent,
}: {
  // E.g. the user can only read the offer
  disabled?: boolean;
  formState: { status: string };
  handleFormState: any;
  initialStatus: null | string;
  // Undefined until the offer is saved
  offerId?: number;
  offerType?: string;
  sent: boolean;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  const dispatch = useDispatch();
  const statusTypes = useSelector((state: any) => state.offers.statusTypes);

  useEffect(() => {
    dispatch(fetchStatusTypes());
  }, []);

  const isOpportunity = offerType === OPPORTUNITY;

  // An approved offer and a converted opportunity are final
  const offerIsApproved =
    isOrderedStatus(initialStatus) || isConvertedStatus(initialStatus);

  // An opportunity has statuses of its own. A sent offer cannot be reopened, a new
  // revision has to be created instead.
  // The statuses of an opportunity in the order of its life: open, then the closings
  // The statuses of the offers in the order of their life: open, ready to send, sent,
  // ordered, rejected
  // A saved offer or preliminary offer can be made a material offer of its suspended
  // ceilings. It is not a status: the offer keeps its own, both may be with the customer.
  const canMakeMaterialOffer =
    offerId !== undefined &&
    (offerType === OFFER || offerType === PRELIMINARY_OFFER);

  const statusOptions = isOpportunity
    ? OPPORTUNITY_STATUSES.filter((status) => statusTypes.includes(status))
    : OFFER_STATUSES.filter(
        (status) =>
          statusTypes.includes(status) &&
          !(sent && (status === OPEN || status === READY_TO_SEND)) &&
          // A preliminary offer is ordered by converting it into an offer
          !(offerType === PRELIMINARY_OFFER && status === ORDERED_AS_MATERIAL)
      );
  const options = canMakeMaterialOffer
    ? [...statusOptions, TO_MATERIAL_OFFER]
    : statusOptions;

  const [confirmIsOpen, setConfirmIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const onChange = (event: any) => {
    if (event.target.value === TO_MATERIAL_OFFER) {
      setConfirmIsOpen(true);
      return;
    }

    handleFormState(event);
  };

  const createMaterialOffer = async () => {
    if (offerId === undefined || creating) {
      return;
    }

    setCreating(true);
    try {
      const result = await apiOffers.materialOffers.post(offerId);
      // The new material offer opens; the offer stays as it was
      window.location.assign(`/offers/${result.id}/${MATERIAL_OFFER}`);
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:materialOffer.Create failed`))
      );
      setCreating(false);
    }
  };

  return (
    <>
      <SelectInputTranslated
        disabled={offerIsApproved || disabled}
        key="status"
        label={t(`${translations.offers}:Status`)}
        labels={{
          [TO_MATERIAL_OFFER]: t(`${translations.offers}:materialOffer.Option`),
        }}
        name="status"
        onChange={onChange}
        options={options}
        required
        translationPath={getStatusTranslationPath(offerType, offerIsApproved)}
        value={formState.status}
      />
      <ModalConfirm
        showing={confirmIsOpen}
        setShowing={() => setConfirmIsOpen(false)}
        header={t(`${translations.offers}:materialOffer.Title`)}
        text={t(`${translations.offers}:materialOffer.Confirm`)}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={createMaterialOffer}
      />
    </>
  );
};

export default Status;
